// src/pages/speaking-clubs/SpeakingClubsList.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Mic, Pencil, Trash2, Users, Loader2, Languages } from "lucide-react";
import { speakingClubsAPI, flattenApiError } from "../../services/speakingClubService";

export default function SpeakingClubsList() {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await speakingClubsAPI.list();
      setClubs(data.clubs || []);
    } catch (err) {
      setError(flattenApiError(err.response?.data));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClubs();
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await speakingClubsAPI.remove(deleteTarget.id);
      setDeleteTarget(null);
      fetchClubs();
    } catch (err) {
      setError(flattenApiError(err.response?.data));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
            <Mic className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">نوادي المحادثة</h1>
            <p className="text-sm text-gray-500">{clubs.length} نادي</p>
          </div>
        </div>
        <button
          onClick={() => navigate("/dashboard/speaking-clubs/create")}
          className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2.5 rounded-xl hover:bg-emerald-700 transition-colors font-medium"
        >
          <Plus className="w-4 h-4" />
          إضافة نادي
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm text-right">
          {error}
        </div>
      )}

      {clubs.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Mic className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>لا توجد نوادي محادثة بعد</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {clubs.map((club) => (
            <div
              key={club.id}
              className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => navigate(`/dashboard/speaking-clubs/${club.id}/edit`)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="تعديل"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(club)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="حذف"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-right flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 truncate">{club.title}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {club.teacher_name}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 justify-end">
                <span className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full font-medium">
                  <Languages className="w-3 h-3" />
                  {club.language_name}
                </span>
                <span
                  className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    club.is_free
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {club.is_free ? "مجاني" : `${club.price} ريال`}
                </span>
                <span
                  className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    club.is_full
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {club.booked_count} / {club.max_students} طالب
                  {club.is_full ? " (مكتمل)" : ""}
                </span>
                {!club.is_active && (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-gray-200 text-gray-600 font-medium">
                    غير نشط
                  </span>
                )}
              </div>

              {club.schedules?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 justify-end">
                  {club.schedules.map((s) => (
                    <span
                      key={s.id}
                      className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full"
                    >
                      {s.day_display} {String(s.time).slice(0, 5)}
                    </span>
                  ))}
                </div>
              )}

              <button
                onClick={() => navigate(`/dashboard/speaking-clubs/${club.id}/bookings`)}
                className="w-full flex items-center justify-center gap-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                <Users className="w-4 h-4" />
                عرض الحجوزات
              </button>
            </div>
          ))}
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-center text-gray-900 mb-2">تأكيد الحذف</h3>
            <p className="text-gray-500 text-center text-sm mb-6">
              هل أنت متأكد من حذف نادي "{deleteTarget.title}"؟ سيتم حذف كل حجوزاته أيضًا.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
              >
                إلغاء
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
